import { observable, configure, action } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';

configure({ enforceActions: 'always' });

class NewsletterStore extends LocalModel {
    constructor() {
        super('newsletter', {
            namePlural: 'newsletters',
            sort: '-updatedDate',
            limit: 100,
            api: {
                search: {
                    url: '/api/newsletters/',
                    params: {
                        limit: 100,
                        sort: '-updatedDate',
                    },
                },
                load: {
                    url: '/api/newsletters/',
                    params: {},
                },
                save: {
                    url: '/api/newsletters/',
                    params: {},
                },
                delete: {
                    url: '/api/newsletters/',
                    params: {},
                },
            },
        });
    }

    @observable newsletters = [];

    @observable newsletter = {};

    @observable newNewsletter = {};

    @observable searchResults = [];

    @action
    async loadDrafts() {
        const response = await util.fetchApi('/api/newsletters/', { method: 'GET' }, { sort: '-updatedDate', limit: 100 });
        if (response?.status === 200) {
            this.updateKeyValue('newsletters', response.data || []);
        }
        return response;
    }

    @action
    async loadDraft(id) {
        const response = await util.fetchApi(`/api/newsletters/${id}`, { method: 'GET' });
        if (response?.status === 200) {
            const doc = Array.isArray(response.data) ? response.data[0] : response.data;
            this.updateKeyValue('newsletter', doc || {});
            return doc;
        }
        return null;
    }

    @action
    async saveDraft({
        id, name = '', subject = '', previewText = '', blocks = [],
    }) {
        const isNew = !id;
        const url = isNew ? '/api/newsletters/' : `/api/newsletters/${id}`;
        const payload = isNew
            ? {
                name, subject, previewText, blocks,
            }
            : {
                name, subject, previewText, 'blocks-overwrite': blocks,
            };
        const response = await util.fetchApi(url, { method: isNew ? 'POST' : 'PATCH' }, payload);
        if (response?.status === 201 || response?.status === 202) {
            const saved = response.data?.newsletter || response.data || {};
            this.updateKeyValue('newsletter', saved);
            return saved;
        }
        return null;
    }

    @action
    async markSent(id, count) {
        const response = await util.fetchApi(`/api/newsletters/${id}`, { method: 'PATCH' }, {
            status: 'sent',
            lastSentDate: new Date().toISOString(),
            lastSentCount: count,
        });
        return response;
    }

    @observable send = {};

    @observable sendRecipients = [];

    @observable sendRecipientsTotal = 0;

    @observable sendSkipped = [];

    @observable sendSkippedTotal = 0;

    @observable sendOpened = [];

    @observable sendOpenedTotal = 0;

    @action
    async startSend(newsletterId, payload) {
        const response = await util.fetchApi(`/api/newsletters/${newsletterId}/send`, { method: 'POST' }, payload);
        return response;
    }

    @action
    async testSend(newsletterId, payload) {
        const response = await util.fetchApi(`/api/newsletters/${newsletterId}/test-send`, { method: 'POST' }, payload);
        return response;
    }

    @action
    async loadSend(sendId) {
        const response = await util.fetchApi(`/api/newsletters/sends/${sendId}`, { method: 'GET' });
        if (response?.status === 200) {
            const doc = Array.isArray(response.data) ? response.data[0] : response.data;
            this.updateKeyValue('send', doc || {});
            return doc;
        }
        return null;
    }

    // Not an @action: it writes no observables. Everything that does goes through the two
    // actions below, which call updateKeyValue AFTER the await — direct assignment there
    // would violate MobX strict mode.
    fetchSendRecipients(sendId, state, extra = {}) {
        const params = { limit: 100, ...extra };
        if (state) {
            params.state = state;
        }
        return util.fetchApi(`/api/newsletters/sends/${sendId}/recipients`, { method: 'GET' }, params);
    }

    @action
    async loadSendRecipients(sendId, state) {
        const response = await this.fetchSendRecipients(sendId, state);
        if (response?.status === 200) {
            this.updateKeyValue('sendRecipients', response.data || []);
            // The route caps what it returns but reports the true count, so the report can
            // say "showing 100 of 4000" instead of passing a truncated list off as complete.
            this.updateKeyValue('sendRecipientsTotal', response.total || 0);
        }
        return response;
    }

    @action
    async loadSendSkipped(sendId) {
        const response = await this.fetchSendRecipients(sendId, 'skipped');
        if (response?.status === 200) {
            this.updateKeyValue('sendSkipped', response.data || []);
            this.updateKeyValue('sendSkippedTotal', response.total || 0);
        }
        return response;
    }

    // Rows with a first-open timestamp, newest open first (the report's "who opened" table).
    @action
    async loadSendOpened(sendId) {
        const response = await this.fetchSendRecipients(sendId, '', { opened: 1 });
        if (response?.status === 200) {
            this.updateKeyValue('sendOpened', response.data || []);
            this.updateKeyValue('sendOpenedTotal', response.total || 0);
        }
        return response;
    }

    @action
    async cancelSend(sendId) {
        return util.fetchApi(`/api/newsletters/sends/${sendId}/cancel`, { method: 'POST' });
    }
}

const store = new NewsletterStore();
export default store;
